import { QueryClient, useMutation, useQueryClient } from '@tanstack/react-query'
import type { ReviewInput } from '@skop-app/lib-domain/types'

import { getAmplifyClient } from '@/services/amplifyClient'
import type {
  CreateFieldInput,
  CreateOptionInput,
  DeleteFieldInput,
  FieldItem,
  FullPropType,
  Property,
  PropertyFieldInput,
  SupportedKeys,
} from '@/services/property'
import { buildFieldMutationValueInput } from '@/services/property/field.value'
import * as queries from '@/services/graphql'
import { UpdateFieldInput } from '@/types'

import { applyCreatedReviewCaches } from './reviewCreatedCache'

export { applyCreatedReviewCaches }

const client = getAmplifyClient()

const invalidateListQueries = (queryClient: QueryClient) => {
  queryClient.invalidateQueries({
    queryKey: ['lists'],
    refetchType: 'all',
  })
  queryClient.invalidateQueries({
    queryKey: ['lists-all'],
    refetchType: 'all',
  })
}

export const useCreateDefinition = () => {
  return useMutation({
    mutationKey: ['createDefinition'],
    mutationFn: createDefinition,
  })
}

export const useUpdateDefinition = () => {
  return useMutation({
    mutationKey: ['updateDefinition'],
    mutationFn: updateDefinition,
  })
}

export const useDeleteProperty = () => {
  return useMutation({
    mutationKey: ['deleteProperty'],
    mutationFn: deleteProperty,
  })
}

export const useCreatePropertyField = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationKey: ['createProperty'],
    mutationFn: createPropertyField,
    onSuccess: (_, variables) => {
      if (variables.type === 'LIST') {
        invalidateListQueries(queryClient)
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['properties'] })
    },
  })
}

export const useCreateOption = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationKey: ['createOption'],
    mutationFn: createOption,
    onMutate: (variables) => {
      updateCacheOnCreate(queryClient, variables)
    },
  })
}

export const useUpdateField = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationKey: ['updateField'],
    mutationFn: updateField,
    onSuccess(_, variables) {
      if (variables.type === 'LIST') {
        invalidateListQueries(queryClient)
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['properties'] })
    },
  })
}

export const useDeleteField = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationKey: ['deleteField'],
    mutationFn: deleteField,
    onMutate: (variables) => {
      removeDeletedFieldFromPropertiesCache(queryClient, variables)
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: ['properties'],
        refetchType: 'all',
      })
      invalidateListQueries(queryClient)
    },
  })
}

export const useDeleteList = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationKey: ['deleteList'],
    mutationFn: deleteList,
    onMutate: (variables) => {
      updateCacheOnDeleteList(queryClient, variables)
    },
    onSuccess: () => {
      // Invalidate lists cache to refresh the options list page
      invalidateListQueries(queryClient)
    },
  })
}

type PropertyInput = {
  id: string
  name: string
  organizationPropertiesId: string
  version: string
}

const createDefinition = async (input: PropertyInput) => {
  try {
    await client.graphql({
      query: queries.createPropertyMutation,
      variables: {
        input,
      },
    })
  } catch (e) {
    console.error(e)
    throw e
  }
}

const updateDefinition = async (
  input: Partial<PropertyInput> & { id: string },
) => {
  try {
    await client.graphql({
      query: queries.updatePropertyMutation,
      variables: {
        input,
      },
    })
  } catch (e) {
    console.error(e)
    throw e
  }
}

const updateCacheOnCreate = (
  queryClient: QueryClient,
  variables:
    | PropertyFieldInput
    | CreateFieldInput
    | CreateOptionInput
    | CreateFieldWithReviewInput,
) => {
  queryClient.cancelQueries({
    queryKey: ['properties'],
  })

  const previousValues = queryClient.getQueriesData<FullPropType>({
    queryKey: ['properties'],
  })

  previousValues.forEach(([queryKey, data]) => {
    const rawFields = data?.fields
    const isItemsShape =
      rawFields &&
      typeof rawFields === 'object' &&
      'items' in rawFields &&
      Array.isArray((rawFields as { items: unknown[] }).items)
    const currentItems = isItemsShape
      ? (rawFields as { items: { id: string; field: unknown }[] }).items
      : Array.isArray(rawFields)
        ? rawFields
        : []
    const relationId =
      'propertyFieldsId' in variables
        ? variables.propertyFieldsId
        : variables.id
    const newItem = { id: relationId, field: variables }

    const nextFields = isItemsShape
      ? {
          ...(rawFields as { items: unknown[] }),
          items: [...currentItems, newItem],
        }
      : Array.isArray(rawFields)
        ? [...rawFields, newItem]
        : { items: [newItem] }

    queryClient.setQueryData(queryKey, {
      ...data,
      fields: nextFields,
    })
  })

  if ('review' in variables) {
    applyCreatedReviewCaches(queryClient, variables.review)
  }

  const listPreviousValues = queryClient.getQueriesData<FieldItem[]>({
    queryKey: ['lists'],
  })
  listPreviousValues.forEach(([queryKey]) => {
    queryClient.setQueryData(
      queryKey,
      (oldData: { pages: { items: FieldItem[] }[] }) => {
        if (!oldData)
          return {
            pages: [
              {
                items: [variables],
              },
            ],
          }
        const newPages = {
          ...oldData,
          pages: oldData.pages.map((page, index) => {
            if (index === oldData.pages.length - 1)
              return {
                ...page,
                items: [...page.items, variables],
              }
            return page
          }),
        }
        return newPages
      },
    )
  })
}

const updateCacheOnDeleteList = (
  queryClient: QueryClient,
  variables: { id: string; items: Property[] },
) => {
  queryClient.cancelQueries({
    queryKey: ['lists'],
  })

  const listPreviousValues = queryClient.getQueriesData({
    queryKey: ['lists'],
  })

  const arrayToRemove = variables.items.map((item) => item.id)

  listPreviousValues.forEach(([queryKey]) => {
    queryClient.setQueryData(
      queryKey,
      (oldData: { pages: { items: FieldItem[] }[] }) => {
        if (!oldData) return oldData

        return {
          ...oldData,
          pages: oldData.pages.map((page) => ({
            ...page,
            items: page.items.filter(
              (entry) =>
                !arrayToRemove.includes(entry.id) && entry.id !== variables.id,
            ),
          })),
        }
      },
    )
  })
}

const toSupportedKeysByKey = (supportedKeys: SupportedKeys[]) =>
  supportedKeys.reduce<Record<string, SupportedKeys>>((acc, property) => {
    const key = property.key || property.id
    if (key) acc[key] = property
    return acc
  }, {})

export const removeDeletedFieldFromPropertiesCache = (
  queryClient: QueryClient,
  variables: DeleteFieldInput,
) => {
  if (!variables.id) return

  queryClient.cancelQueries({
    queryKey: ['properties'],
  })

  const previousValues = queryClient.getQueriesData({
    queryKey: ['properties'],
  })

  previousValues.forEach(([queryKey, data]) => {
    if (!data || typeof data !== 'object') return

    if (
      'supportedKeys' in data &&
      Array.isArray((data as { supportedKeys?: unknown }).supportedKeys)
    ) {
      const current = data as {
        supportedKeys: SupportedKeys[]
        lists?: [string, string][]
        listDefinitions?: Array<{
          key: string
          listId: string
          propertyFieldId: string
        }>
      } & Record<string, unknown>

      const nextSupportedKeys = current.supportedKeys.filter(
        (property) =>
          property.id !== variables.id &&
          property.propertyFieldsId !== variables.propertyFieldsId,
      )

      if (nextSupportedKeys.length === current.supportedKeys.length) return

      queryClient.setQueryData(queryKey, {
        ...current,
        supportedKeys: nextSupportedKeys,
        byKey: toSupportedKeysByKey(nextSupportedKeys),
        lists:
          variables.type === 'LIST'
            ? (current.lists ?? []).filter(
                ([key, listId]) =>
                  key !== variables.key && listId !== variables.id,
              )
            : current.lists,
        listDefinitions:
          variables.type === 'LIST'
            ? (current.listDefinitions ?? []).filter(
                (definition) =>
                  definition.key !== variables.key &&
                  definition.propertyFieldId !== variables.id,
              )
            : current.listDefinitions,
        refKey: nextSupportedKeys.find((property) => property.isRef)?.key || '',
      })
      return
    }

    if (!('fields' in data)) return

    const current = data as FullPropType & Record<string, unknown>
    const rawFields = current.fields
    const isItemsShape =
      rawFields &&
      typeof rawFields === 'object' &&
      'items' in rawFields &&
      Array.isArray((rawFields as { items?: unknown }).items)

    if (!Array.isArray(rawFields) && !isItemsShape) return

    const nextFields = Array.isArray(rawFields)
      ? (rawFields as Array<{ id?: string; field?: { id?: string } }>).filter(
          (entry) =>
            entry?.id !== variables.propertyFieldsId &&
            entry?.field?.id !== variables.id,
        )
      : {
          ...(rawFields as {
            items: Array<{ id: string; field?: { id?: string } }>
          }),
          items: (
            rawFields as {
              items: Array<{ id: string; field?: { id?: string } }>
            }
          ).items.filter(
            (entry) =>
              entry.id !== variables.propertyFieldsId &&
              entry.field?.id !== variables.id,
          ),
        }

    const currentLength = Array.isArray(rawFields)
      ? rawFields.length
      : (rawFields as { items: unknown[] }).items.length
    const nextLength = Array.isArray(nextFields)
      ? nextFields.length
      : nextFields.items.length

    if (currentLength === nextLength) return

    queryClient.setQueryData(queryKey, {
      ...current,
      fields: nextFields,
    })
  })
}

export const createPropertyField = async (
  params: PropertyFieldInput & {
    propertyID: string
    propertyFieldsId: string
    organizationFieldsId: string
  },
) => {
  const { propertyID, propertyFieldsId: id, ...input } = params

  const createFieldInput = {
    ...input,
    ...buildFieldMutationValueInput({
      value: input.value,
      type: input.type,
    }),
  }

  try {
    await client.graphql({
      query: queries.createPropertyFieldMutation,
      variables: {
        createFieldInput,
        createPropertyFieldsInput: {
          id,
          propertyID,
          fieldID: input.id,
        },
      },
    })
  } catch (e) {
    console.error(e)
    throw e
  }
}

export type CreateFieldWithReviewInput = CreateOptionInput & {
  review: ReviewInput
  propertyKey: string
}

const createOption = async (
  params: CreateOptionInput | CreateFieldWithReviewInput,
) => {
  const {
    id,
    label,
    value,
    metadata,
    fieldOptionsId,
    optionOptionsId,
    userID,
    organizationID,
    status,
  } = params

  const review = 'review' in params ? params.review : undefined

  const input = {
    id,
    label,
    userID,
    ...(organizationID && { organizationID }),
    ...buildFieldMutationValueInput({ value }),
    ...(fieldOptionsId && { fieldOptionsId }),
    ...(optionOptionsId && { optionOptionsId }),
    ...(metadata && { metadata }),
    ...(status && { status }),
  }
  try {
    await client.graphql({
      query: queries.createOptionMutation,
      variables: {
        input,
      },
    })
    if (review) {
      await client.graphql({
        query: queries.createReviewMutation,
        variables: {
          input: {
            ...review,
          },
        },
      })
    }
  } catch (e) {
    console.error(e)
    throw e
  }
}

// IF key is present, it's a field. Otherwise, it's an option
const updateField = async (params: UpdateFieldInput) => {
  const {
    id,
    label,
    value,
    key,
    type,
    metadata,
    status,
    fieldOptionsId,
    optionOptionsId,
    organizationID,
  } = params
  const mutation = key
    ? queries.updateFieldMutation
    : queries.updateOptionMutation

  try {
    await client.graphql({
      query: mutation,
      variables: {
        input: {
          id,
          ...(label && { label }),
          ...buildFieldMutationValueInput({ value, type }),
          ...(metadata && { metadata }),
          ...(status && { status }),
          ...(!optionOptionsId &&
            fieldOptionsId && { fieldOptionsId, optionOptionsId: null }),
          ...(!fieldOptionsId &&
            optionOptionsId && { optionOptionsId, fieldOptionsId: null }),
          ...(organizationID && { organizationID }),
        },
      },
    })
  } catch (e) {
    console.error(e)
    throw e
  }
}

const deleteField = async (params: DeleteFieldInput) => {
  const { id, key, propertyFieldsId } = params
  try {
    // Only properties have a relation
    if (key) {
      await client.graphql({
        query: queries.deletePropertyFieldsMutation,
        variables: {
          input: {
            id: propertyFieldsId,
          },
        },
      })
    }
    // Avoid to destroy only reference to options
    if (!key) {
      await client.graphql({
        query: queries.deleteOptionMutation,
        variables: {
          input: {
            id,
          },
        },
      })
    }
  } catch (e) {
    console.error(e)
    throw e
  }
}

const deleteList = async (params: { id: string; items: Property[] }) => {
  const { id, items } = params
  try {
    for (const option of items) {
      await client.graphql({
        query: queries.deleteOptionMutation,
        variables: {
          input: {
            id: option.id,
          },
        },
      })
    }

    // Finally, delete the list field itself
    await client.graphql({
      query: queries.deleteFieldMutation,
      variables: {
        input: {
          id,
        },
      },
    })
  } catch (e) {
    console.error(e)
    throw e
  }
}

const deleteProperty = async (input: { id: string }) => {
  try {
    await client.graphql({
      query: queries.deletePropertyMutation,
      variables: {
        input,
      },
    })
  } catch (e) {
    console.error(e)
    throw e
  }
}

type CreatePropFields = {
  id: string
  propertyID: string
  fieldID: string
}

export const createPropertyFields = async (input: CreatePropFields) => {
  try {
    return await client.graphql({
      query: queries.createPropertyFieldsMutation,
      variables: {
        input,
      },
    })
  } catch (e) {
    console.error(e)
    throw e
  }
}
